import { createApp } from 'vue';
import PrimeVue from 'primevue/config';
//@ts-ignore
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import ConfirmPopup from 'primevue/confirmpopup';
import type { Component } from 'vue';
import '/styles/primeicons.css';
import { ConfirmationService } from 'primevue';

const registry: Record<string, () => Promise<{ default: Component }>> = {
    //@ts-ignore
    MeetingRecord: () => import('../components/MeetingRecord.vue'),
    //@ts-ignore
    SideBar: () => import('../components/SideBar.vue'),
    //@ts-ignore
    InviteEmailsInput: () => import('../components/InviteEmailsInput.vue'),
    //@ts-ignore
    FeatureView: () => import('../components/FeatureView.vue'),
};

function parseProps(el: Element) {
    const raw = el.getAttribute('data-props');
    if (!raw) return {};
    try {
        return JSON.parse(raw);
    } catch (e) {
        console.error('Failed to parse props for element:', el, e);
        return {};
    }
}

async function mountComponents() {
    const nodes: NodeListOf<HTMLElement> = document.querySelectorAll(
        '[data-vue]:not([data-vue-mounted])',
    );
    for (const el of nodes) {
        if (!el.isConnected) {
            continue;
        }
        const name: string | undefined = el.dataset.vue;
        if (!name) {
            continue;
        }
        const loader = registry[name];
        if (!loader) {
            continue;
        }
        try {
            const componentModule = await loader();
            const component = componentModule.default;
            if (!component) {
                console.error(
                    `[vue-mount] Module for ${name} has no default export`,
                );
                continue;
            }
            const props = parseProps(el);
            // Defer actual mount to wait until next frame to avoid race with template processing
            await new Promise((r) => requestAnimationFrame(r));
            // Re-check after async gap — element may have been detached by navigation or HMR
            if (!el.isConnected || el.hasAttribute('data-vue-mounted')) {
                continue;
            }
            // Clear any stale DOM nodes to prevent Vue unmount errors on fragment nodes
            el.innerHTML = '';
            const app = createApp(component, props);
            app.use(PrimeVue, {
                theme: {
                    preset: JRPreset,
                    options: {
                        prefix: 'jr',
                        darkModeSelector: false || 'none',
                        cssLayer: false,
                    },
                },
            });
            app.use(ToastService);
            app.use(ConfirmationService);
            app.directive('tooltip', Tooltip);
            try {
                app.mount(el);
                el.setAttribute(`data-vue-mounted`, `true`);
            } catch (mountErr) {
                console.error(`[vue-mount] mount error for ${name}`, mountErr);
            }
        } catch (e) {
            console.error(`[vue-mount] failed to load component: ${name}`, e);
        }
    }
}

const JRPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{Blue.50}',
            100: '{Blue.100}',
            200: '{Blue.200}',
            300: '{Blue.300}',
            400: '{Blue.400}',
            500: '{Blue.500}',
            600: '{Blue.600}',
            700: '{Blue.700}',
            800: '{Blue.800}',
            900: '{Blue.900}',
            950: '{Blue.950}',
        },
    },
});

await mountComponents();
